
import type { AllowedTransition, TransitionInputSpec } from '../model/types';

export type ApprovalTarget =
  | { mode: 'auto' }
  | { mode: 'committee'; committeeId: string }
  | { mode: 'person'; approverId: string };

export type ApprovalTargetMode = ApprovalTarget['mode'];

export interface ApprovalAccessScope {
  canViewChat: boolean;
  canViewAttachments: boolean;
}

export interface ApprovalTransitionSpec {
  approverKey: string | null;
  committeeKey: string | null;
  subjectKey: string | null;
  subjectLabel: string;
  subjectRequired: boolean;
  scopeKey: string | null;
  scopeLabel: string;
  rest: TransitionInputSpec[];
}

const APPROVAL_KINDS: TransitionInputSpec['kind'][] = ['approval_committee', 'approval_access_scope'];

export function parseApprovalTransition(t: AllowedTransition): ApprovalTransitionSpec | null {
  const inputs = t.inputs ?? [];
  if (!inputs.some((i) => APPROVAL_KINDS.includes(i.kind))) return null;

  const committee = inputs.find((i) => i.kind === 'approval_committee') ?? null;
  const scope = inputs.find((i) => i.kind === 'approval_access_scope') ?? null;
  const approver = inputs.find((i) => i.kind === 'user_picker') ?? null;
  const subject =
    inputs.find((i) => i.kind === 'text' && i.key === 'subject') ??
    inputs.find((i) => i.kind === 'text') ??
    null;

  const claimed = new Set([committee?.key, scope?.key, approver?.key, subject?.key]);

  return {
    approverKey: approver?.key ?? null,
    committeeKey: committee?.key ?? null,
    subjectKey: subject?.key ?? null,
    subjectLabel: subject?.label ?? 'Предмет согласования',
    subjectRequired: Boolean(subject?.required),
    scopeKey: scope?.key ?? null,
    scopeLabel: scope?.label ?? 'Что видит согласующий',
    rest: inputs.filter((i) => !claimed.has(i.key)),
  };
}

export function availableModes(spec: ApprovalTransitionSpec): ApprovalTargetMode[] {
  const modes: ApprovalTargetMode[] = ['auto', 'committee'];
  if (spec.approverKey) modes.push('person');
  return modes;
}

export function committeeDelivery(spec: ApprovalTransitionSpec): 'transition' | 'round' {
  return spec.committeeKey ? 'transition' : 'round';
}

export function approvalTargetProblem(
  spec: ApprovalTransitionSpec,
  target: ApprovalTarget,
): string | null {
  if (target.mode === 'committee') {
    return target.committeeId ? null : 'Выберите комитет';
  }
  if (target.mode === 'person') {
    if (!spec.approverKey) return 'На этом переходе нельзя выбрать согласующего';
    return target.approverId ? null : 'Выберите согласующего';
  }
  return null;
}

export interface ApprovalFormValues {
  target: ApprovalTarget;
  subject: string;
  scope: ApprovalAccessScope;
  rest: Record<string, unknown>;
}

export function buildApprovalInputs(
  spec: ApprovalTransitionSpec,
  values: ApprovalFormValues,
): Record<string, unknown> {
  const inputs: Record<string, unknown> = { ...values.rest };

  if (values.target.mode === 'person' && spec.approverKey) {
    inputs[spec.approverKey] = values.target.approverId;
  }
  if (values.target.mode === 'committee' && spec.committeeKey) {
    inputs[spec.committeeKey] = values.target.committeeId;
  }

  const subject = values.subject.trim();
  if (spec.subjectKey && subject) inputs[spec.subjectKey] = subject;

  if (spec.scopeKey && !(values.scope.canViewChat && values.scope.canViewAttachments)) {
    inputs[spec.scopeKey] = { ...values.scope };
  }
  return inputs;
}
